import React, { useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

import { Modal } from '@/components/ui'
import { formatDate } from '@/utils/formatters'
import RenewTaskForm from '@/features/renew-tasks/RenewTaskForm'

const STATUS_BADGES = {
  cancelled: { label: 'Annulée', className: 'badge-ghost' }
}

/**
 * Composant unifié pour afficher les tâches (ProcessTask et RenewTask)
 *
 * @param {Object} task - Tâche à afficher
 * @param {Function} onComplete - Callback lors de la complétion
 * @param {Function} onStart - Callback lors du démarrage (optionnel, pour ProcessTasks)
 * @param {string} type - Type de tâche: 'renew' | 'process'
 * @param {boolean} showColorIndicator - Afficher le cercle de couleur (défaut: false)
 * @param {boolean} showUrgencyBadge - Afficher le badge d'urgence (défaut: true)
 * @param {boolean} showDetails - Afficher les détails supplémentaires (défaut: false)
 */
const TaskItem = ({
  task,
  onComplete,
  onStart,
  type = 'renew',
  showColorIndicator = false,
  showUrgencyBadge = true,
  showDetails = false,
  typeConfig = {}
}) => {
  const [isUpdating, setIsUpdating] = useState(false)

  // États pour la confirmation de check
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  // Gérer la fermeture du formulaire
  const handleFormClose = () => {
    handleHide()
  }

  // Gérer le succès du formulaire
  const handleFormSuccess = () => {
    handleHide()
    // La tâche est déjà marquée comme complétée dans le formulaire
  }

  // Fonction pour rendre le formulaire
  const renderTaskForm = () => {
    if (!task) return null

    return (
      <RenewTaskForm
        task={task}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
      />
    )
  }

  // Confirmer la complétion de la tâche
  const confirmCompletion = async () => {
    setIsUpdating(true)
    try {
      // Si la tâche est en attente et qu'il y a onStart, d'abord la démarrer
      if (onStart && task.status === 'pending') {
        await onStart(task.id)
      }
      // Si elle est en cours ou qu'il n'y a pas d'onStart, compléter directement
      else if (task.status === 'in_progress' || !onStart) {
        await onComplete(task.id)
      }
    } finally {
      setIsUpdating(false)
      setShowConfirmDialog(false)
    }
  }

  // Annuler la suppression
  const cancelCompletion = () => {
    setIsUpdating(false)
    setShowConfirmDialog(false)
  }

  const handleCheckboxChange = async (e) => {
    e.preventDefault()

    if (task.status === 'completed' || task.status === 'cancelled') return

    if(typeConfig.hasForm) {
      handleShow()
    } else {
      setShowConfirmDialog(true)
    }
  }

  const isCompleted = task.status === 'completed'
  const isCancelled = task.status === 'cancelled'
  const isDisabled = isUpdating || isCancelled || isCompleted

  // Badge d'urgence (pour les RenewTasks)
  // 2026-07-06 commenté car fonctionnalité non voulue par le client
  // const getUrgencyBadge = () => {
  //   if (!showUrgencyBadge || type !== 'renew' || task.days_until_due == null) return null

  //   if (task.days_until_due < 0) {
  //     return <span className="badge badge-error badge-sm">En retard</span>
  //   }
  //   if (task.days_until_due <= 7) {
  //     return <span className="badge badge-error badge-sm">Urgent</span>
  //   }
  //   if (task.days_until_due <= 30) {
  //     return <span className="badge badge-warning badge-sm">Bientôt</span>
  //   }
  //   return null
  // }

  // Cercle de couleur avec la couleur de l'utilisateur assigné
  const getColorIndicator = () => {
    if (!showColorIndicator) return null

    const colors = {
      pending: 'bg-red-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      cancelled: 'bg-gray-400'
    }

    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé'
    }

    return (
      <div
        className={`w-4 h-4 rounded-full flex-shrink-0 ${colors[task.status] || 'bg-gray-300'}`}
        title={labels[task.status] || task.status}
      />
    )
  }

  // Nom du bien/entretien/intervention
  const getRelateName = () => {
    if (!showDetails) return null

    if (type === 'renew' && task.renewable) {
      return `${task.description}`
    }
    if (type === 'process' && task.bien) {
      const bienId = `#${task.bien_id}`
      return bienId
    }
    return null
  }

  const getTitle = () => {
    if(type === 'process') {
      return task.title
    }
    if(type === 'renew') {
      return `${getRelateName()} pour "${task.title}"`
    }
    return null
  }

  const getDescription = () => {
    if(type === "process" && task.description?.length > 0 ) {
      return <div className="flex flex-col p-2">
        {task.description}
      </div>
    }
    if(type === 'renew') {
      return task.description
    }
    return null
  }

  const getBienURL = () => {
    if (task.renewable?.type === "Bien") {
      return task.renewable.links?.show
    }
    if(task.renewable?.bien?.links) {
      return task.renewable?.bien?.links.show
    }
    return null
  }

  const statusBadge = STATUS_BADGES[task.status] || null

  return (
    <div className={`flex items-center p-2 rounded transition-colors border ${
        isCompleted
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:bg-gray-50'
    }`}>
      {/* Checkbox */}
      <input
        type="checkbox"
        className={`checkbox mt-1 flex-shrink-0 ${
          isCompleted ? 'checkbox-success' : 'checkbox-primary'
        }`}
        checked={isCompleted}
        onChange={handleCheckboxChange}
        disabled={isDisabled}
      />

      <div className="flex-1 min-w-0 ml-3">

        <div className="flex">
          <div className={`flex-1 font-medium ${
            isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
          }`}>
          { getBienURL() ?
              <div className='flex flex-nowrap items-center p-1 hover:bg-primary-300'>
                <a
                  className='btn btn-square btn-ghost btn-xs p-1 hover:bg-primary-400'
                  href={getBienURL()}
                  title="Aller sur la fiche du bien"
                >
                  <InformationCircleIcon/>
                </a>
                <div className="flex flex-col">
                  {task.title}
                </div>
              </div> :
              <div>{task.title}</div>
          }
          </div>

          <div className="flex-shrink-0 ml-2 flex items-center gap-2 flex-wrap">
            {getColorIndicator()}
          </div>

        </div>

        {showDetails && (getRelateName() || task.due_date) && (
          <div>
            {getRelateName() && <div className={`${
              isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
            }`}>{getRelateName()} </div>}
            {task.due_date && (
              <span className={`text-xs ${
                  task.overdue ? 'text-red-600 font-semibold' : 'text-gray-500'
              }`}>
                Échéance: {formatDate(task.due_date)}
                {showDetails && type !== "renew" && isCompleted && task.completed_at && (
                  <span className="text-xs text-green-600 ml-2">
                    Réalisé le {formatDate(task.completed_at)}
                  </span>
                )}
              </span>
            )}
          </div>
        )}


      </div>

      {(!isCompleted && typeConfig.hasForm) && (
        <Modal
          open={isOpen}
          onClose={handleHide}
          backdrop={false}
          ref={dialogRef}
          className="max-w-full rounded-none"
        >
          <Modal.Header showCloseButton={false} />
          <Modal.Body>
            {renderTaskForm()}
          </Modal.Body>
        </Modal>
      )}
      {(!isCompleted && showConfirmDialog && !typeConfig.hasForm) && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmer</h3>
            <p className="py-4">"{getTitle()} est-elle réalisée ?</p>
            <div className="modal-action">
              <button onClick={confirmCompletion} className="btn btn-secondary">Confirmer</button>
              <button onClick={cancelCompletion} className="btn btn-outline">Annuler</button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  )
}

export default TaskItem
